// Dedicated content for the Market & Customer Intelligence capability page.
// Rendered by pages/IntelligencePage.jsx at /capabilities/market-customer-intelligence

export const INTELLIGENCE_PAGE = {
  slug: "market-customer-intelligence",
  title: "Market & Customer Intelligence",
  metaTitle: "Market Research Consultants India | BLEZZINGS",
  metaDescription:
    "Market research and customer intelligence in India — opportunity sizing, buyer insights, segmentation, competitive intelligence and foresight that drive decisions.",
  heroSubhead: "Turn market truth into commercial advantage.",
  heroParas: [
    "We help businesses understand markets, customers, competitors and emerging opportunities — and translate that intelligence into clearer choices on where to play, who to target and how to win.",
    "From market sizing and customer research to segmentation, competitive intelligence and future growth opportunities, we replace assumptions with evidence — and evidence with action.",
  ],
  heroCta: "Discuss Your Intelligence Needs",

  introLead: "Better decisions start with knowing what is true.",
  introParas: [
    "Businesses rarely suffer from a lack of data.",
    "They suffer from too many signals, conflicting opinions and assumptions that have never been tested.",
    "Before entering a market, launching a product, setting a price, choosing a customer segment, repositioning a brand or committing marketing investment, leaders need answers to fundamental questions:",
  ],
  introQuestions: [
    "How large is the real opportunity?",
    "Where are the most attractive growth pools?",
    "Which customers should we prioritise?",
    "What do they truly need and value?",
    "What drives — or prevents — purchase?",
    "How are competitors winning?",
    "Where are the gaps in the market?",
    "How is customer behaviour changing?",
    "What emerging shifts could create the next growth opportunity?",
  ],
  introClosing:
    "BLEZZINGS brings together research, data, competitive intelligence and commercial judgement to answer those questions — and turn the answers into decisions.",

  offersLead: "Intelligence for the decisions that drive growth.",
  offers: [
    {
      name: "Market Opportunity & Sizing",
      lead: "Know where the opportunity really is.",
      body: "Size markets, assess category attractiveness and identify the geographies, segments, categories and growth pools worth pursuing.",
      listLabel: "We help with:",
      list: [
        "Market sizing and forecasting",
        "TAM, SAM and addressable opportunity",
        "Category and sub-category analysis",
        "Market attractiveness assessment",
        "Geographic opportunity assessment",
        "Growth-pool identification",
        "White-space analysis",
        "Market-entry opportunity assessment",
      ],
      closing: null,
    },
    {
      name: "Customer, Consumer & Buyer Insights",
      lead: "Understand what really drives customer choice.",
      body: "Go beyond demographics to uncover customer needs, motivations, behaviours, perceptions, pain points and unmet needs.",
      listLabel: "We help understand:",
      list: [
        "Customer needs and unmet needs",
        "Attitudes and behaviours",
        "Purchase drivers and barriers",
        "Product usage and occasions",
        "Brand and category perceptions",
        "Value expectations",
        "Customer pain points",
        "B2B buyer needs and decision criteria",
      ],
      closing:
        "The objective is not simply to know customers better. It is to understand what the business should do differently because of what we learn.",
    },
    {
      name: "Segmentation & Targeting",
      lead: "Find the customers worth winning.",
      body: "Not every customer represents the same opportunity. We identify distinct customer segments, quantify their attractiveness and determine which audiences the business should prioritise for acquisition, growth and retention.",
      listLabel: "We help with:",
      list: [
        "Customer segmentation",
        "Consumer segmentation",
        "B2B account segmentation",
        "Ideal Customer Profile development",
        "Segment sizing",
        "Segment attractiveness assessment",
        "Target-market prioritisation",
        "Customer personas and need states",
        "Segment-specific value propositions",
      ],
      closing:
        "The result is greater clarity on who to target, who not to target and how the proposition should change by segment.",
    },
    {
      name: "Competitive Intelligence",
      lead: "Know where competitors win — and where they are vulnerable.",
      body: "We decode competitors beyond surface-level benchmarking to understand the strategies and commercial systems behind their performance.",
      listLabel: "We analyse:",
      list: [
        "Business and growth strategy",
        "Product and service portfolios",
        "Pricing and promotions",
        "Customer propositions",
        "Positioning and messaging",
        "Distribution and channels",
        "Sales models",
        "Digital and e-commerce presence",
        "Marketing activity",
        "Customer perceptions and reviews",
        "Capabilities and competitive strengths",
        "Strategic gaps and vulnerabilities",
      ],
      closing:
        "The objective is not to copy competitors. It is to understand the competitive landscape well enough to make a different and stronger strategic choice.",
    },
    {
      name: "Customer Decision & Journey Intelligence",
      lead: "Understand how customers actually choose.",
      body: "Customers rarely move neatly from awareness to purchase. We map how customers discover, evaluate, compare, decide, buy, use and remain loyal — identifying the moments, barriers and influences that determine conversion.",
      listLabel: "We help uncover:",
      list: [
        "Customer decision journeys",
        "Path-to-purchase",
        "Buying criteria",
        "Decision triggers",
        "Barriers and objections",
        "Information sources",
        "Channel preferences",
        "Critical touchpoints",
        "B2B buying committees",
        "Shopper behaviour",
        "Post-purchase experience",
        "Loyalty and switching drivers",
      ],
      closing:
        "This gives sales, marketing, product and channel teams a clearer view of where and how customer decisions can be influenced.",
    },
    {
      name: "Trends, Foresight & Growth Opportunities",
      lead: "See what is changing before it becomes obvious.",
      body: "Markets move before financial reports show it. We identify emerging customer, category, competitive, technology and channel shifts — then determine what those changes could mean commercially.",
      listLabel: "We help with:",
      list: [
        "Consumer and customer trend analysis",
        "Category trends",
        "Emerging business models",
        "Technology shifts",
        "Channel evolution",
        "Competitive disruption",
        "Behavioural change",
        "Future scenarios",
        "Emerging opportunity spaces",
        "Growth-theme identification",
      ],
      closing:
        "We don't stop at: “Here is the trend.” We ask: “What does this mean for the business — and what should we do about it?”",
    },
  ],

  methodLead: "The method depends on the question.",
  methodIntro: [
    "We don't begin with a preferred research technique.",
    "We begin with the business decision that needs to be made, then determine what evidence is required to make it with confidence.",
  ],
  methods: [
    {
      name: "Primary Research",
      body: "Direct evidence from customers, consumers, buyers, channel partners and other stakeholders.",
      listLabel: "Depending on the question, this may include:",
      list: [
        "Depth interviews",
        "Customer interviews",
        "B2B stakeholder interviews",
        "Focus groups",
        "Ethnographic research",
        "Customer observation",
        "Expert interviews",
        "Quantitative surveys",
        "Structured market studies",
      ],
    },
    {
      name: "Secondary & Data Research",
      body: "Rigorous synthesis of existing market, category, company and economic information.",
      listLabel: "This may include:",
      list: [
        "Industry and category research",
        "Market and company analysis",
        "Government and trade data",
        "Financial information",
        "Published databases and reports",
        "Import/export information",
        "Competitive benchmarking",
        "Market and category modelling",
      ],
    },
    {
      name: "Digital & Behavioural Signals",
      body: "Digital environments often reveal behaviours traditional research misses.",
      listLabel: "Where relevant, we analyse signals such as:",
      list: [
        "Search behaviour",
        "Customer reviews",
        "Marketplace activity",
        "Digital competitor presence",
        "Social conversations",
        "Online pricing and assortment",
        "Customer questions and complaints",
        "Digital category trends",
      ],
    },
    {
      name: "Strategic Analysis & Synthesis",
      body: "Data becomes useful only when patterns are interpreted.",
      listLabel: "We connect evidence across sources to identify:",
      list: [
        "What is happening",
        "Why it is happening",
        "What matters most",
        "What is likely to change",
        "Where the opportunity lies",
        "What the business should do next",
      ],
    },
  ],

  approachLead: "From question to evidence to action.",
  approach: [
    { title: "Frame the Decision", body: "We start by defining the business decision — not simply the research brief. What exactly does management need to know, decide or change?" },
    { title: "Build the Evidence", body: "We determine the right combination of primary research, secondary research, market data, competitive intelligence and behavioural signals." },
    { title: "Find the Signal", body: "We separate meaningful patterns from noise and identify the customer, market and competitive dynamics that matter." },
    { title: "Translate Insight into Choices", body: "We convert findings into implications and clear choices on customers, markets, propositions, portfolios, pricing, channels, brands or growth opportunities." },
    { title: "Activate", body: "Where required, the same BLEZZINGS team can take those choices forward into strategy and execution." },
  ],
  approachClosing:
    "Research should not end with a presentation. It should change what the business does next.",

  deliverLead: "Intelligence designed to support a decision.",
  deliverIntro: "Depending on the engagement, outputs may include:",
  deliver: [
    "Market and category assessment",
    "Market-sizing models",
    "Opportunity maps",
    "Growth-pool analysis",
    "Customer-insight reports",
    "Segmentation models",
    "Ideal Customer Profiles",
    "Customer personas and need states",
    "Competitive landscapes",
    "Competitor deep dives",
    "Benchmarking frameworks",
    "Customer decision journeys",
    "Purchase-driver analysis",
    "White-space maps",
    "Trend and foresight reports",
    "Future-growth scenarios",
    "Strategic implications",
    "Prioritised recommendations",
    "Management decision frameworks",
  ],
  deliverClosing:
    "The deliverable is not simply information. It is clarity on what the evidence means and what decision it supports.",

  whenLead: "Bring us in when an important decision needs better evidence.",
  whenIntro:
    "Market & Customer Intelligence engagements are particularly valuable when you are:",
  whenBullets: [
    "Evaluating a new market or category",
    "Entering a new geography",
    "Launching a new product or service",
    "Deciding which customer segments to prioritise",
    "Reworking your growth strategy",
    "Building a go-to-market strategy",
    "Repositioning a brand",
    "Reviewing your product portfolio",
    "Setting or changing pricing",
    "Facing new or aggressive competitors",
    "Experiencing slowing growth or declining market share",
    "Trying to understand poor customer conversion",
    "Looking for the next growth opportunity",
    "Testing a major management assumption before investing behind it",
  ],
  whenAudience:
    "We typically work with CEOs, founders, business heads, strategy leaders, CMOs, sales leaders, category heads, product teams and P&L owners.",

  whyLead: "Intelligence built to change a decision — not fill a deck.",
  whyIntro: [
    "Traditional research can stop with the findings.",
    "BLEZZINGS goes further because market and customer intelligence is part of a broader commercial system.",
  ],
  whyBlocks: [
    {
      name: "Decision-First Research",
      paras: [
        "We start with what management needs to decide, then design the research around the evidence required.",
      ],
    },
    {
      name: "Commercial Interpretation",
      paras: [
        "We don't simply report what customers said or what the market data shows.",
        "We connect findings to revenue, margins, customers, competition and growth.",
      ],
    },
    {
      name: "Strategy Connected to Intelligence",
      paras: ["The same insights can flow directly into decisions on:"],
      highlight: "Portfolio. Pricing. Go-to-Market. Channels. Sales. Brand. Marketing. Digital.",
    },
    {
      name: "From Insight to Execution",
      paras: [
        "When required, BLEZZINGS doesn't hand over the research and leave.",
        "We can work alongside your team to translate the intelligence into strategy, activation and measurable commercial outcomes.",
      ],
    },
  ],
  whyClosing: "One partner. From understanding the market to acting on it.",

  relatedLead:
    "Intelligence is the starting point. Growth comes from what you do with it.",
  related: [
    {
      title: "Strategy & Commercial Transformation",
      body: "Turn market and customer intelligence into choices on portfolio, pricing, channels, go-to-market and sales.",
      to: "/capabilities/strategy-commercial-transformation",
    },
    {
      title: "Brand, Marketing & Communications",
      body: "Translate customer insight into sharper positioning, propositions, brand strategy and communications.",
      to: "/capabilities/brand-marketing-communications",
    },
    {
      title: "Digital & Commerce",
      body: "Use customer behaviour and decision intelligence to improve digital acquisition, commerce, conversion, retention and loyalty.",
      to: "/capabilities/digital-commerce",
    },
  ],

  faqs: [
    { q: "What is Market & Customer Intelligence?", a: "Market & Customer Intelligence is the systematic understanding of markets, customers, competitors and emerging trends to support better business decisions. It combines research, data analysis and strategic interpretation rather than relying on any single information source." },
    { q: "How is Market & Customer Intelligence different from traditional market research?", a: "Market research typically focuses on gathering and analysing information. BLEZZINGS starts with the commercial decision that needs to be made and works backwards to determine what research and evidence are required. We then translate the findings into strategic implications and recommended actions." },
    { q: "Do you conduct primary market research?", a: "Yes. Depending on the business question, primary research can include customer interviews, depth interviews, focus groups, ethnographic research, stakeholder interviews, surveys and structured quantitative studies." },
    { q: "Do you conduct secondary research and market sizing?", a: "Yes. We undertake market sizing, category and industry analysis, competitive research, company analysis, market modelling and synthesis of relevant secondary data sources." },
    { q: "Can BLEZZINGS help us size a new market or category?", a: "Yes. Market opportunity assessment can include overall market size, addressable market, growth rates, customer segments, competitive intensity, channel structure, white spaces and commercial attractiveness." },
    { q: "Can you help us understand our competitors?", a: "Yes. Competitive Intelligence can cover competitor strategy, portfolio, pricing, positioning, channels, sales models, marketing, digital presence, customer perceptions and other commercial capabilities." },
    { q: "Can you help identify our ideal target customers?", a: "Yes. Through segmentation, customer research and opportunity analysis, we can identify and prioritise customer groups based on their needs, value, attractiveness and fit with your proposition." },
    { q: "Do you work with both B2B and B2C businesses?", a: "Yes. The research techniques and decision journeys differ, but the underlying objective is the same: understand the market and customer well enough to make better commercial decisions." },
    { q: "Do you only provide research, or can you also help implement the recommendations?", a: "Both. BLEZZINGS can undertake a focused Market & Customer Intelligence engagement or continue into strategy, portfolio, pricing, go-to-market, brand, sales, marketing, digital and commerce execution." },
  ],

  ctaHeading: "Replace assumptions with evidence. Turn evidence into action.",
  ctaBody:
    "If you need to understand a market, customer, competitor or growth opportunity before making an important commercial decision, let's start with the question you need answered.",
  ctaLabel: "Discuss Your Intelligence Needs",
};
